import * as ko from "knockout";
import {IMessage} from "../../../index";
import {isMessageFlowNameValidForCopyWizard} from "../../ruleset/copy-wizard/copyWizardStrictNames";

/**
 * Name + target collision state for one message flow row in {@link component/messages/copy-wizard/main.ts}.
 * Collision scope is **message flow vs message flow only** (per product spec).
 *
 * **Strong name checks:** aligned with `component/messages/mutable.ts` via {@link isMessageFlowNameValidForCopyWizard}.
 */
export class MessageFlowNameConflictRowVm {
    readonly hadConflictInitially: boolean;
    readonly hadInitialBatchDuplicateName: boolean;
    readonly draftName: KnockoutObservable<string>;

    constructor(
        readonly message: IMessage,
        private readonly getTargetMessages: () => IMessage[],
        private readonly getOtherDraftNames: () => string[],
        hadInitialBatchDuplicateName: boolean,
    ) {
        this.draftName = ko.observable(message.name);
        const sourceNameTrimmed = message.name.trim();
        this.hadConflictInitially = this.getTargetMessages().some(
            (t) => t.name.trim() === sourceNameTrimmed,
        );
        this.hadInitialBatchDuplicateName = hadInitialBatchDuplicateName;
    }

    readonly conflictTarget = ko.pureComputed((): IMessage | null => {
        const trimmed = this.draftName().trim();
        return this.getTargetMessages().find((t) => t.name.trim() === trimmed) ?? null;
    });

    readonly hasFormatViolation = ko.pureComputed((): boolean => {
        const trimmed = this.draftName().trim();
        if (!trimmed) {
            return false;
        }
        return !isMessageFlowNameValidForCopyWizard(trimmed);
    });

    readonly hasBlockingConflict = ko.pureComputed((): boolean => {
        const my = this.draftName().trim();
        if (!my) {
            return true;
        }
        if (!isMessageFlowNameValidForCopyWizard(my)) {
            return true;
        }
        if (this.getTargetMessages().some((t) => t.name.trim() === my)) {
            return true;
        }
        return this.getOtherDraftNames().includes(my);
    });

    readonly badgeState = ko.pureComputed((): "conflict" | "noconflict" =>
        this.hasBlockingConflict() ? "conflict" : "noconflict",
    );

    readonly showNameInput = ko.pureComputed(
        () =>
            this.hasBlockingConflict() ||
            this.hadConflictInitially ||
            this.hadInitialBatchDuplicateName ||
            this.hasFormatViolation(),
    );
}
